import { useRef } from 'react';

import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverTrigger,
} from '@vimeo/bokeh';
import { CloseXCircleFilled, SearchMagnifier } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import { useDebouncedCallback } from '@vimeo/ui/hooks/use-debounced-callback';

import { SearchResults, type SearchResultsProps } from '../SearchResults';

const SEARCH_DELAY = 400;

type SearchBarProps = SearchResultsProps & {
  textColor: string;
  onSearch?: (text: string) => void;
  onResetSearch?: () => void;
  onResultClick?: () => void;
  onDismissSearch?: () => void;
};

export const SearchBar = ({
  textColor,
  canLoadMore,
  resultsPending,
  resultsList,
  searchError,
  searchedTerm,
  onResetSearch,
  onResultClick,
  onSearch,
  onLoadMoreSearchResults,
  onDismissSearch,
}: SearchBarProps) => {
  // Send query to Beluga when search text is updated; Includes a debounce to
  // to ensure the request is only sent after the user has paused typing
  const handleSearch = (text: string) => onSearch?.(text);
  const debouncedSearchChange = useDebouncedCallback(handleSearch, SEARCH_DELAY);

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleSearch(value);
    debouncedSearchChange.debouncedCallback(value);
  };

  const hasSearchText = searchedTerm.trim() !== '';
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const clearSearchBtnRef = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);

  // If an empty search input is blurred, dismiss search and focus the next element
  const dismissIfNoSearchText = () => !hasSearchText && onDismissSearch?.();

  // If there are no results or results are pending shift focus to
  // the popover content
  const focusResultsContainer = () => {
    if (resultsPending || !resultsList?.length) {
      popoverContentRef.current?.focus();
    }
  };

  // Switch focus to the 'clear search' button
  const focusClearSearchBtn = () => {
    clearSearchBtnRef.current?.focus();
  };

  // Switch focus to the search input field
  const focusInputField = () => {
    inputFieldRef.current?.focus();
  };

  // Clear the search term while keeping the input field in focus
  const handleClearSearch = () => {
    onResetSearch?.();
    focusInputField();
  };

  // Handle Tab and Shift+Tab when the 'clear search' button is focused
  const onClearSearchKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        e.preventDefault();
        if (resultsList?.length) {
          const firstSearchResultElem = resultsListRef?.current?.firstElementChild;
          (firstSearchResultElem as HTMLElement)?.focus();
        } else {
          popoverContentRef.current?.focus();
        }
      } else {
        onDismissSearch?.();
      }
    }
  };

  return (
    <Popover placement="bottom-start" autoFocus={false} isOpen matchWidth>
      <PopoverTrigger>
        <InputGroup onBlur={dismissIfNoSearchText}>
          <Input
            cursor="text"
            aria-haspopup="dialog"
            onChange={onSearchInputChange}
            placeholder="Search"
            size="md"
            value={searchedTerm}
            backdropFilter="blur(var(--vimeo-blur-md))"
            autoFocus
            color={textColor}
            variant="outlined"
            _placeholder={{ color: textColor }}
            _focus={{
              outlineColor: textColor,
            }}
            _active={{
              outlineColor: textColor,
            }}
            onBlur={focusResultsContainer}
            ref={inputFieldRef}
          />
          <InputLeftElement>
            <SearchMagnifier color={textColor} />
          </InputLeftElement>
          {hasSearchText ? (
            <InputRightElement>
              <IconButton
                aria-label={translate('clear search')}
                icon={<CloseXCircleFilled />}
                onKeyDown={onClearSearchKeyDown}
                onClick={handleClearSearch}
                size="sm"
                variant="tertiary"
                color={textColor}
                tabIndex={-1}
                _focus={{
                  outlineColor: 'focus !important',
                  outlineStyle: 'solid !important',
                }}
                ref={clearSearchBtnRef}
              />
            </InputRightElement>
          ) : null}
        </InputGroup>
      </PopoverTrigger>
      <SearchResults
        canLoadMore={canLoadMore}
        resultsList={resultsList}
        resultsPending={resultsPending}
        searchError={searchError}
        searchedTerm={searchedTerm}
        onLoadMoreSearchResults={onLoadMoreSearchResults}
        onResultClick={onResultClick}
        onDismissSearch={onDismissSearch}
        onTabFromResultsList={focusClearSearchBtn}
        onTabShiftFromResultsList={focusInputField}
        popoverContentRef={popoverContentRef}
        resultsListRef={resultsListRef}
      />
    </Popover>
  );
};
